import React, { useContext, useEffect, useRef, useState } from 'react'

import './Filter.scss'
import { FilterOptions } from '../FilterOptions/FilterOptions'
import FiltersContext from '../../../../../Context/FiltersContext'
import { useTranslation } from 'react-i18next'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterListIcon from '@mui/icons-material/FilterList'

interface IFilter {
  filterKey: string
  category: string
  options: { label: string; value: any }[]
  filterable?: boolean
}

export const Filter = ({
  filterKey,
  category,
  options,
  filterable = false,
}: IFilter) => {
  const { t }: any = useTranslation()
  const componentRef = useRef<HTMLDivElement>(null)
  const { setFiltersSelected, filterOpen, setFilterOpen } =
    useContext(FiltersContext)
  const [selected, setSelected] = useState<{ label: string; value: any }[]>([
    {
      label: t('filters.all'),
      value: t('filters.all'),
    },
  ])
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)

  useEffect(() => {
    if (filterOpen !== '' && filterOpen !== category) {
      setIsFilterOpen(false)
    }
  }, [filterOpen])

  const handleClickOutside = (event: MouseEvent) => {
    // Verifica si el clic fue fuera del componente
    if (
      componentRef.current &&
      !componentRef.current.contains(event.target as Node)
    ) {
      setIsFilterOpen(false) // Cierra el componente o realiza cualquier acción
    }
  }

  useEffect(() => {
    // Agrega el listener cuando el componente está montado
    document.addEventListener('mousedown', handleClickOutside)

    // Limpia el listener cuando el componente se desmonta
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleToggle = (value: { label: string; value: any }) => {
    let newSelected = [...selected]

    // Encuentra el índice basado en value.label
    const currentIndex = newSelected.findIndex(
      (item) => item.label === value.label
    )

    // Si no está en la lista, lo agrega; si está, lo elimina
    if (currentIndex === -1) {
      newSelected.push(value)
    } else {
      newSelected.splice(currentIndex, 1)
    }

    // Maneja el caso especial de 'filters.all'
    if (newSelected.length > 0 && newSelected[0].label === t('filters.all')) {
      newSelected.shift() // Remueve el 'filters.all' si hay otros elementos
    } else if (
      newSelected
        .map((selectes) => selectes.label)
        .includes(t('filters.all')) ||
      newSelected.length === 0
    ) {
      newSelected = [{ label: t('filters.all'), value: t('filters.all') }] // Si está vacío o contiene 'filters.all', asegura que sea el único
    }

    // Actualiza el estado
    setFiltersSelected((prev: any) => ({
      ...prev,
      [filterKey]: newSelected,
    }))

    setSelected(newSelected)
  }

  const handleFilterOpen = () => {
    const isOpening = !isFilterOpen

    if (isOpening && filterOpen !== category) {
      setFilterOpen(category)
    } else {
      setFilterOpen('')
    }

    setIsFilterOpen(!isFilterOpen)
  }

  return (
    <div ref={componentRef} className="dropdown-container">
      <div
        className={`dropdown-category ${
          isFilterOpen ? 'dropdown-category_open' : ''
        }`}
        onClick={handleFilterOpen}
      >
        <span className="dropdown-category_text">{category}</span>
        <span className="dropdown-icon">
          <FilterListIcon sx={{ marginRight: 1, fontSize: 20 }} />
        </span>
      </div>
      {isFilterOpen ? (
        <div className="dropdown-filterOptions">
          <FilterOptions
            options={options}
            handleToggle={handleToggle}
            selected={selected}
            className="dropdown-menu-item"
          />
        </div>
      ) : null}
    </div>
  )
}
